import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { concatMap, filter, switchMap, take, takeUntil } from 'rxjs/operators';

import {
  ConsolidatedFeature,
  DcTypes,
  EmsKpiProperties,
  EmsPowerBalanceProperties,
  EmsPowerBalanceProperty,
  EmsPowerBalanceTypes,
  EmsPowerFlowsProperties,
  Feature,
  FeatureNames,
  FeaturePowerLimitationOverview,
  FeaturePropertyKey,
  HeatingRodHeatingCommands,
  HeatingRodHeatingProperties,
  HeatingRodStatusProperties,
  IsNumberValueProperty,
  NumberValue,
  PhotovoltaicStatusProperties,
  PowerLimitationType,
  ThermalCockpitProperties,
} from '@vi-iot/client';

import { GTMService } from '@services';
import {
  DevicesStore,
  DeviceTypes,
  GatewayStore,
  Helpers,
  InstallationDetailsStore,
  InstallationFeaturesStore,
} from '@store';

import { HemsFlowType } from '../../../@store/energy-historical-data/energy-historical-data.helpers';
import {
  EnergyFlowDirections,
  EnergyFlowInType,
  EnergyFlowOutType,
} from '../../../operational-data-common/translation-pipes/translate-energy-flow-type.pipe';

@Component({
  selector: 'app-energy-flow',
  templateUrl: './energy-flow.component.html',
  styleUrls: ['./energy-flow.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnergyFlowComponent implements OnInit, OnDestroy {
  readonly FLOW_TYPES = HemsFlowType;
  readonly ENERGY_FLOW_IN_TYPE = EnergyFlowInType;
  readonly ENERGY_FLOW_OUT_TYPE = EnergyFlowOutType;
  readonly ENERGY_FLOW_DIRECTIONS = EnergyFlowDirections;
  readonly emsPowerBalanceTypes = EmsPowerBalanceTypes;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly possibleRunningRodStatusValues = ['1', '2', '3'];
  readonly PowerLimitationType = PowerLimitationType;
  getHeatingRodStatusValue = Helpers.getHeatingRodStatusValue;
  stateOfCharge$: Observable<Feature<IsNumberValueProperty, never>>;
  stateOfChargeConsolidated$: Observable<Feature<ConsolidatedFeature, never>>;
  indicators$: Observable<Feature<EmsKpiProperties, never>>;
  powerBalance: Feature<EmsPowerBalanceProperties, never>;
  powerFlows$: Observable<Feature<EmsPowerFlowsProperties, never>>;
  pvStatus$: Observable<Feature<PhotovoltaicStatusProperties, never>>;
  heatingRodStatus$: Observable<Feature<HeatingRodStatusProperties, never>>;
  heatingSpfTotal$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingRodHeating$: Observable<Feature<HeatingRodHeatingProperties, HeatingRodHeatingCommands>>;
  heatingRodDhw$: Observable<Feature<HeatingRodHeatingProperties, HeatingRodHeatingCommands>>;

  thermalCockpit$: Observable<Feature<ThermalCockpitProperties, never>>;
  consumptionLimitation$: Observable<FeaturePowerLimitationOverview>;
  productionLimitation$: Observable<FeaturePowerLimitationOverview>;

  isThermalFlow = false;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public gatewayStore: GatewayStore,
    public devicesStore: DevicesStore,
    private gtmService: GTMService,
    private cdr: ChangeDetectorRef,
    private installationFeaturesStore: InstallationFeaturesStore,
    private installationDetailsStore: InstallationDetailsStore
  ) {}

  ngOnInit(): void {
    this.thermalCockpit$ = this.installationFeaturesStore.selectInstallationGatewayFeature(
      FeatureNames.thermalCockpit
    );

    this.indicators$ = this.installationFeaturesStore.selectInstallationHemsFeature(
      FeatureNames.emsKeyPerformanceIndicators
    );
    this.heatingSpfTotal$ = this.installationFeaturesStore.selectInstallationDeviceFeatureForHeatingDevice(
      FeatureNames.heatingSpfTotal
    );
    this.heatingRodHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeatureForHeatingDevice(
      FeatureNames.heatingRodHeating
    );
    this.heatingRodDhw$ = this.installationFeaturesStore.selectInstallationDeviceFeatureForHeatingDevice(
      FeatureNames.heatingRodDhw
    );
    this.heatingRodStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingRodStatus
    );
    this.consumptionLimitation$ = this.installationDetailsStore.getFeaturePowerLimitationOverview(
      PowerLimitationType.consumption
    );
    this.productionLimitation$ = this.installationDetailsStore.getFeaturePowerLimitationOverview(
      PowerLimitationType.production
    );
    this.powerFlows$ = this.installationFeaturesStore.selectInstallationHemsFeature<
      EmsPowerFlowsProperties,
      never
    >(FeatureNames.emsPowerFlows);
    this.pvStatus$ = this.devicesStore.getPhotovoltaicDevice$.pipe(
      filter((device) => !!device),
      switchMap((device) =>
        this.installationFeaturesStore.selectInstallationDeviceFeature<
          PhotovoltaicStatusProperties,
          never
        >(FeatureNames.photovoltaicStatus, device.id)
      )
    );
    this.setThermalFlowTab();

    // Stream set below in order to prevent .html from overcomplexity
    this.devicesStore.shouldUseConsolidatedFeature$
      .pipe(take(1), takeUntil(this.ngUnsubscribe$))
      .subscribe((shouldUseConsolidatedFeature) => {
        if (shouldUseConsolidatedFeature) {
          this.stateOfChargeConsolidated$ = this.installationFeaturesStore.selectInstallationDeviceFeatureByDeviceType(
            FeatureNames.stateOfChargeConsolidated,
            DeviceTypes.ElectricityStorage
          );
        } else {
          this.stateOfCharge$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
            FeatureNames.stateOfCapacityNominal
          );
        }
      });
  }

  onSelectFlowType(selectedFlowType: HemsFlowType): void {
    this.isThermalFlow = selectedFlowType === HemsFlowType.Thermal;
  }

  trackClose(): void {
    this.gtmService.track('onClose', 'Energy flows', 'energyFlows');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  // transforming object to feature-like object to support value and unit pipe
  getPropertiesAsFeatureLike<T>(value: EmsPowerBalanceProperty): Feature<T, never> {
    const valueForPipeConsumption: NumberValue = {
      type: DcTypes.number, // EmsPowerBalanceTypes are not supported and number strategy will not pick it
      value: value.value,
      unit: value.unit,
    };

    return (<unknown>{
      properties: { value: valueForPipeConsumption },
    }) as Feature<T, never>;
  }

  private setThermalFlowTab(): void {
    this.installationFeaturesStore
      .selectInstallationHemsFeature<EmsPowerBalanceProperties, never>(FeatureNames.emsPowerBalance)
      .pipe(
        concatMap((powerBalance, requestIndex) => {
          if (requestIndex === 0) {
            this.isThermalFlow = !powerBalance;
          }

          return of(powerBalance);
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe((powerBalance) => {
        this.powerBalance = powerBalance;
        this.cdr.detectChanges();
      });
  }
}
